// グローバルステート管理

import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import {
  Tenant,
  User,
  Project,
  RoutineTask,
  TaskInstance,
  ActionHistory,
  FilterState,
  TaskTemplate,
  Task,
  WorkspaceMembership,
} from '../types';
import {
  mockTenants,
  mockTenant,
  mockUsers,
  mockProjects,
  mockRoutineTasks,
  mockTaskInstances,
  mockActionHistory,
  mockTaskTemplates,
  mockWorkspaceMemberships,
} from '../utils/mockData';
import { calculateNextDueDate } from '../utils/taskGenerator';

interface AppContextType {
  // 認証関連
  isAuthenticated: boolean;
  currentUser: User | null;
  setAuthUser: (user: User) => void;
  login: (email: string, password: string) => boolean;
  logout: () => void;
  signUp: (workspaceName: string, userName: string, email: string, password: string) => boolean;
  demoLogin: (email: string) => boolean;
  
  // OTP認証関連
  sendOTP: (email: string) => { success: boolean; message: string };
  verifyOTP: (email: string, otp: string) => boolean;
  sendSignUpOTP: (email: string) => { success: boolean; message: string };
  verifySignUpOTP: (email: string, otp: string, workspaceName: string, userName: string) => boolean;
  
  // ワークスペース関連
  currentTenantId: string;
  tenant: Tenant;
  tenants: Tenant[];
  workspaceMemberships: WorkspaceMembership[];
  switchWorkspace: (tenantId: string) => void;
  getUserWorkspaces: (userId: string) => Tenant[];
  
  // 既存の機能
  users: User[];
  projects: Project[];
  routineTasks: RoutineTask[];
  taskInstances: TaskInstance[];
  actionHistory: ActionHistory[];
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  completeTask: (taskId: string, userId: string) => void;
  uncompleteTask: (taskId: string) => void;
  addProject: (project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) => string;
  updateProject: (projectId: string, updates: Partial<Project>) => void;
  deleteProject: (projectId: string) => void;
  archiveProject: (projectId: string) => void;
  unarchiveProject: (projectId: string) => void;
  addRoutineTask: (task: Omit<RoutineTask, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateRoutineTask: (taskId: string, updates: Partial<RoutineTask>) => void;
  deleteRoutineTask: (taskId: string) => void;
  updateTaskInstance: (taskId: string, updates: Partial<TaskInstance>) => void;
  deleteTaskInstance: (taskId: string) => void;
  taskTemplates: TaskTemplate[];
  
  // タスクテンプレート管理
  addTaskTemplate: (template: Omit<TaskTemplate, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateTaskTemplate: (templateId: string, updates: Partial<TaskTemplate>) => void;
  deleteTaskTemplate: (templateId: string) => void;
  addTaskToTemplate: (templateId: string, task: Task) => void;
  
  // テナント・ユーザー管理
  updateTenant: (tenantId: string, updates: Partial<Tenant>) => void;
  addUser: (user: Omit<User, 'id' | 'createdAt'>) => void;
  updateUser: (userId: string, updates: Partial<User>) => void;
  deleteUser: (userId: string) => void;
  
  // ゲストユーザー招待機能
  inviteGuestToProject: (projectId: string, email: string, name: string) => boolean;
  removeProjectMember: (projectId: string, userId: string) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  // 認証関連
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  
  // OTP管理
  const [otpStore, setOtpStore] = useState<{ [email: string]: { code: string; expiresAt: Date } }>({});
  
  // Initialize auth state from storage on mount
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const userInfo = localStorage.getItem('user_info');
    
    if (token && userInfo) {
      try {
        const user = JSON.parse(userInfo);
        setIsAuthenticated(true);
        setCurrentUser(user);
        
        // Set workspace if user has tenant IDs
        if (user.tenantIds && user.tenantIds.length > 0) {
          setCurrentTenantId(user.tenantIds[0]);
        }
      } catch (error) {
        console.error('Error initializing auth from storage:', error);
        // Clear invalid storage data
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user_info');
      }
    }
  }, []);
  
  // 現在のワークスペースID
  const [currentTenantId, setCurrentTenantId] = useState<string>('tenant-1');
  
  // 全テナント
  const [tenants, setTenants] = useState<Tenant[]>(mockTenants);
  const [workspaceMemberships, setWorkspaceMemberships] = useState<WorkspaceMembership[]>(mockWorkspaceMemberships);
  
  // 現在のテナントを取得
  const tenant = tenants.find(t => t.id === currentTenantId) || mockTenant;
  
  const [allUsers, setAllUsers] = useState<User[]>(mockUsers);
  const [allProjects, setAllProjects] = useState<Project[]>(mockProjects);
  const [allRoutineTasks, setAllRoutineTasks] = useState<RoutineTask[]>(mockRoutineTasks);
  const [allTaskInstances, setAllTaskInstances] = useState<TaskInstance[]>(mockTaskInstances);
  const [allActionHistory, setAllActionHistory] = useState<ActionHistory[]>(mockActionHistory);
  const [allTaskTemplates, setAllTaskTemplates] = useState<TaskTemplate[]>(mockTaskTemplates);
  
  // OTP送信
  const sendOTP = (email: string): { success: boolean; message: string } => {
    // ユーザーが存在するか確認
    const user = allUsers.find(u => u.email === email);
    if (!user) {
      return { success: false, message: 'User not found' };
    }
    
    // 6桁のOTPコードを生成
    const otpCode = Math.floor(100000 + Math.random() * 900000).toString();
    const expiresAt = new Date(Date.now() + 5 * 60 * 1000); // 5分間有効
    
    // OTPを保存
    setOtpStore(prev => ({
      ...prev,
      [email]: { code: otpCode, expiresAt }
    }));
    
    // デモ用：コンソールとアラートでOTPを表示（実際の実装ではメール送信）
    console.log(`🔐 OTP for ${email}: ${otpCode} (expires at ${expiresAt.toLocaleTimeString()})`);
    console.log(`Demo: Your OTP code is ${otpCode}`);
    
    return { success: true, message: `OTP sent to ${email}. Check console for demo code.` };
  };
  
  // OTP検証
  const verifyOTP = (email: string, otp: string): boolean => {
    const storedOTP = otpStore[email];
    
    if (!storedOTP) {
      return false; // OTPが送信されていない
    }
    
    // 有効期限をチェック
    if (new Date() > storedOTP.expiresAt) {
      // 期限切れのOTPを削除
      setOtpStore(prev => {
        const newStore = { ...prev };
        delete newStore[email];
        return newStore;
      });
      return false;
    }
    
    // OTPコードをチェック
    if (storedOTP.code !== otp) {
      return false;
    }
    
    // OTP検証成功 - ログイン処理
    const user = allUsers.find(u => u.email === email);
    if (user) {
      setIsAuthenticated(true);
      setCurrentUser(user);
      
      // Store demo tokens for auth guard
      localStorage.setItem('access_token', `demo_token_${user.id}`);
      localStorage.setItem('user_info', JSON.stringify(user));
      
      // ユーザーの最初のテナントIDに切り替え
      if (user.tenantIds.length > 0) {
        setCurrentTenantId(user.tenantIds[0]);
        localStorage.setItem('current_workspace', user.tenantIds[0]);
      }
      
      // 使用済みOTPを削除
      setOtpStore(prev => {
        const newStore = { ...prev };
        delete newStore[email];
        return newStore;
      });
      
      return true;
    }
    
    return false;
  };
  
  const login = (email: string, password: string): boolean => {
    // ログイン処理（仮実装）
    const user = allUsers.find(u => u.email === email && u.password === password);
    if (user) {
      setIsAuthenticated(true);
      setCurrentUser(user);
      
      // Store demo tokens for auth guard
      localStorage.setItem('access_token', `demo_token_${user.id}`);
      localStorage.setItem('user_info', JSON.stringify(user));
      
      // ユーザーの最初のテナントIDに切り替え
      if (user.tenantIds.length > 0) {
        setCurrentTenantId(user.tenantIds[0]);
        localStorage.setItem('current_workspace', user.tenantIds[0]);
      }
      return true;
    }
    return false;
  };
  
  const demoLogin = (email: string): boolean => {
    // デモログイン処理（仮実装）
    const user = allUsers.find(u => u.email === email);
    if (user) {
      setIsAuthenticated(true);
      setCurrentUser(user);
      
      // Store demo tokens for auth guard
      localStorage.setItem('access_token', `demo_token_${user.id}`);
      localStorage.setItem('user_info', JSON.stringify(user));
      
      // ユーザーの最初のテナントIDに切り替え
      if (user.tenantIds.length > 0) {
        setCurrentTenantId(user.tenantIds[0]);
        localStorage.setItem('current_workspace', user.tenantIds[0]);
      }
      return true;
    }
    return false;
  };

  // Set authenticated user from API login
  const setAuthUser = (user: User) => {
    setIsAuthenticated(true);
    setCurrentUser(user);
  };
  
  const logout = () => {
    setIsAuthenticated(false);
    setCurrentUser(null);
    // Clear ALL localStorage data
    localStorage.clear();
    // Redirect to login page
    window.location.href = '/login';
  };
  
  const signUp = (workspaceName: string, userName: string, email: string, password: string): boolean => {
    // 新規登録処理（仮実装）
    const existingUser = allUsers.find(u => u.email === email);
    if (existingUser) return false;

    const newTenant: Tenant = {
      id: `tenant-${Date.now()}`,
      name: workspaceName,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    setTenants(prev => [...prev, newTenant]);

    const newUser: User = {
      id: `user-${Date.now()}`,
      tenantIds: [newTenant.id],
      email,
      password,
      name: userName,
      role: 'admin',
      projectIds: [],
      emailNotificationEnabled: true,
      createdAt: new Date(),
    };
    setAllUsers(prev => [...prev, newUser]);

    const newMembership: WorkspaceMembership = {
      id: `membership-${Date.now()}`,
      tenantId: newTenant.id,
      userId: newUser.id,
      role: 'admin',
      createdAt: new Date(),
    };
    setWorkspaceMemberships(prev => [...prev, newMembership]);

    setIsAuthenticated(true);
    setCurrentUser(newUser);
    setCurrentTenantId(newTenant.id);
    
    // Store demo tokens for auth guard
    localStorage.setItem('access_token', `demo_token_${newUser.id}`);
    localStorage.setItem('user_info', JSON.stringify(newUser));
    localStorage.setItem('current_workspace', newTenant.id);
    
    return true;
  };
  
  // OTP送信（新規登録用）
  const sendSignUpOTP = (email: string): { success: boolean; message: string } => {
    // 6桁のOTPコードを生成
    const otpCode = Math.floor(100000 + Math.random() * 900000).toString();
    const expiresAt = new Date(Date.now() + 5 * 60 * 1000); // 5分間有効
    
    // OTPを保存
    setOtpStore(prev => ({
      ...prev,
      [email]: { code: otpCode, expiresAt }
    }));
    
    // デモ用：コンソールとアラートでOTPを表示（実際の実装ではメール送信）
    console.log(`🔐 OTP for ${email}: ${otpCode} (expires at ${expiresAt.toLocaleTimeString()})`);
    console.log(`Demo: Your OTP code is ${otpCode}`);
    
    return { success: true, message: `OTP sent to ${email}. Check console for demo code.` };
  };
  
  // OTP検証（新規登録用）
  const verifySignUpOTP = (email: string, otp: string, workspaceName: string, userName: string): boolean => {
    const storedOTP = otpStore[email];
    
    if (!storedOTP) {
      return false; // OTPが送信されていない
    }
    
    // 有効期限をチェック
    if (new Date() > storedOTP.expiresAt) {
      // 期限切れのOTPを削除
      setOtpStore(prev => {
        const newStore = { ...prev };
        delete newStore[email];
        return newStore;
      });
      return false;
    }
    
    // OTPコードをチェック
    if (storedOTP.code !== otp) {
      return false;
    }
    
    // OTP検証成功 - 新規登録処理
    const newTenant: Tenant = {
      id: `tenant-${Date.now()}`,
      name: workspaceName,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    setTenants(prev => [...prev, newTenant]);

    const newUser: User = {
      id: `user-${Date.now()}`,
      tenantIds: [newTenant.id],
      email,
      password: 'guest123', // デモ用仮パスワード
      name: userName,
      role: 'admin',
      projectIds: [],
      emailNotificationEnabled: true,
      createdAt: new Date(),
    };
    setAllUsers(prev => [...prev, newUser]);

    const newMembership: WorkspaceMembership = {
      id: `membership-${Date.now()}`,
      tenantId: newTenant.id,
      userId: newUser.id,
      role: 'admin',
      createdAt: new Date(),
    };
    setWorkspaceMemberships(prev => [...prev, newMembership]);

    setIsAuthenticated(true);
    setCurrentUser(newUser);
    setCurrentTenantId(newTenant.id);
    
    // Store demo tokens for auth guard
    localStorage.setItem('access_token', `demo_token_${newUser.id}`);
    localStorage.setItem('user_info', JSON.stringify(newUser));
    localStorage.setItem('current_workspace', newTenant.id);
    
    // 使用済みOTPを削除
    setOtpStore(prev => {
      const newStore = { ...prev };
      delete newStore[email];
      return newStore;
    });
    
    return true;
  };
  
  // 現在のテナントに属するデータのみをフィルタリング
  const projects = allProjects.filter(p => p.tenantId === currentTenantId);
  const routineTasks = allRoutineTasks.filter(rt => {
    const project = projects.find(p => p.id === rt.projectId);
    return project !== undefined;
  });
  const taskInstances = allTaskInstances.filter(ti => {
    const project = projects.find(p => p.id === ti.projectId);
    return project !== undefined;
  });
  const actionHistory = allActionHistory.filter(ah => ah.tenantId === currentTenantId);
  const taskTemplates = allTaskTemplates.filter(tg => tg.workspaceId === currentTenantId);
  
  // 現在のテナントに所属するユーザーのみ
  const users = allUsers.filter(u => u.tenantIds.includes(currentTenantId));
  
  // ワークスペース切り替え
  const switchWorkspace = (tenantId: string) => {
    setCurrentTenantId(tenantId);
    // フィルターをリセット
    setFilters({
      projectIds: [],
      dateRange: { type: 'week' },
      assigneeIds: [],
      status: 'pending',
    });
  };

  // ユーザーが所属するワークスペースを取得
  const getUserWorkspaces = (userId: string): Tenant[] => {
    const userMemberships = workspaceMemberships.filter(m => m.userId === userId);
    return tenants.filter(t => userMemberships.some(m => m.tenantId === t.id));
  };

  // フィルター状態
  const [filters, setFilters] = useState<FilterState>({
    projectIds: [],
    dateRange: { type: 'week' },
    assigneeIds: [],
    status: 'pending',
  });

  // タスク完了処理（完了トリガー方式）
  const completeTask = (taskId: string, userId: string) => {
    const task = allTaskInstances.find(t => t.id === taskId);
    if (!task) {
      console.error('Task not found:', taskId);
      return;
    }

    // タスクが既に完了している場合は何もしない
    if (task.status === 'completed') {
      console.warn('Task is already completed:', taskId);
      return;
    }

    const completedAt = new Date();
    
    console.log('=== タスク完了処理開始 ===');
    console.log('Task ID:', taskId);
    console.log('Task Name:', task.name);
    console.log('Completed At:', completedAt);
    console.log('User ID:', userId);

    // ルーチンタスク情報を取得
    const routineTask = allRoutineTasks.find(rt => rt.id === task.routineTaskId);
    
    // 次回タスクを生成（完了トリガー方式の核心部分）
    let newTaskInstance: TaskInstance | null = null;
    
    if (routineTask && routineTask.isActive) {
      const nextDueDate = calculateNextDueDate(completedAt, routineTask.repeatPattern);
      console.log('Next Due Date:', nextDueDate);
      
      // 繰り返しなし（none）の場合はnullが返るので次回タスクを生成しない
      if (nextDueDate) {
        newTaskInstance = {
          id: `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          routineTaskId: routineTask.id,
          projectId: routineTask.projectId,
          name: routineTask.name,
          description: routineTask.description,
          occurrenceDate: completedAt,
          dueDate: nextDueDate,
          assigneeIds: routineTask.assigneeIds,
          status: 'pending',
          createdAt: completedAt,
          updatedAt: completedAt,
        };
        console.log('新しいタスクインスタンスを生成:', newTaskInstance.id, 'Due:', nextDueDate);
      } else {
        console.log('繰り返しなし、または次回タスク生成不要');
      }
    } else {
      console.log('ルーチンタスクが見つからないか、非アクティブ');
    }

    // 状態を一括更新（Reactの状態更新をまとめて実行）
    setAllTaskInstances(prev => {
      // 現在のタスクを完了状態に更新
      const updated = prev.map(t =>
        t.id === taskId
          ? { ...t, status: 'completed' as const, completedAt, completerId: userId, updatedAt: completedAt }
          : t
      );
      
      // 次回タスクがあれば追加
      if (newTaskInstance) {
        return [...updated, newTaskInstance];
      }
      
      return updated;
    });

    // アクションヒストリーを一括追加
    const actions: ActionHistory[] = [];
    
    // 完了アクション
    actions.push({
      id: `action-${Date.now()}-complete`,
      tenantId: currentTenantId,
      targetType: 'task_instance',
      targetId: taskId,
      actionType: 'completed',
      executorId: userId,
      executedAt: completedAt,
    });
    
    // 次回タスク生成アクション
    if (newTaskInstance) {
      actions.push({
        id: `action-${Date.now()}-create`,
        tenantId: currentTenantId,
        targetType: 'task_instance',
        targetId: newTaskInstance.id,
        actionType: 'created',
        executorId: userId,
        executedAt: completedAt,
      });
    }
    
    setAllActionHistory(prev => [...actions, ...prev]);
    
    console.log('=== タスク完了処理完了 ===');
  };

  // タスク完了の取り消し
  const uncompleteTask = (taskId: string) => {
    const task = taskInstances.find(t => t.id === taskId);
    if (!task) return;

    // 完了を取り消し
    setAllTaskInstances(prev =>
      prev.map(t =>
        t.id === taskId
          ? { ...t, status: 'pending' as const, completedAt: undefined, completerId: undefined, updatedAt: new Date() }
          : t
      )
    );

    // この完了によって生成された次回タスクを削除
    // （簡略化: 完了日時が一致するタスクを削除）
    if (task.completedAt) {
      setAllTaskInstances(prev =>
        prev.filter(t => {
          if (t.routineTaskId !== task.routineTaskId) return true;
          if (t.status === 'completed') return true;
          // 完了時刻の前後1秒以内に生成されたタスクを削除
          const timeDiff = Math.abs(t.createdAt.getTime() - task.completedAt!.getTime());
          return timeDiff > 1000;
        })
      );
    }

    // アクションヒストリーに記録
    const action: ActionHistory = {
      id: `action-${Date.now()}`,
      tenantId: tenant.id,
      targetType: 'task_instance',
      targetId: taskId,
      actionType: 'uncompleted',
      executorId: currentUser!.id,
      executedAt: new Date(),
    };
    setAllActionHistory(prev => [action, ...prev]);
  };

  // プロジェクト追加
  const addProject = (project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>): string => {
    const newProject: Project = {
      ...project,
      id: `project-${Date.now()}`,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    setAllProjects(prev => [...prev, newProject]);
    return newProject.id;
  };

  // プロジェクト更新
  const updateProject = (projectId: string, updates: Partial<Project>) => {
    setAllProjects(prev =>
      prev.map(p => (p.id === projectId ? { ...p, ...updates, updatedAt: new Date() } : p))
    );
  };

  // プロジェクト削除
  const deleteProject = (projectId: string) => {
    setAllProjects(prev => prev.filter(p => p.id !== projectId));
    // 関連するルーチンタスクとタスクインスタンスも削除
    setAllRoutineTasks(prev => prev.filter(rt => rt.projectId !== projectId));
    setAllTaskInstances(prev => prev.filter(t => t.projectId !== projectId));
  };

  // プロジェクトアーカイブ
  const archiveProject = (projectId: string) => {
    setAllProjects(prev =>
      prev.map(p => (p.id === projectId ? { ...p, isArchived: true, updatedAt: new Date() } : p))
    );
  };

  // プロジェクトアーカイブ解除
  const unarchiveProject = (projectId: string) => {
    setAllProjects(prev =>
      prev.map(p => (p.id === projectId ? { ...p, isArchived: false, updatedAt: new Date() } : p))
    );
  };

  // ルーチンタスク追加
  const addRoutineTask = (task: Omit<RoutineTask, 'id' | 'createdAt' | 'updatedAt'>) => {
    const taskId = `routine-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newTask: RoutineTask = {
      ...task,
      id: taskId,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    setAllRoutineTasks(prev => [...prev, newTask]);

    // 初回タスクインスタンスを生成
    const initialDueDate = calculateNextDueDate(new Date(), task.repeatPattern);
    
    // 繰り返しなしの場合やnullが返る場合は、initialDueDateを使用するか翌日をデフォルトとする
    let dueDate: Date;
    if (initialDueDate) {
      dueDate = initialDueDate;
    } else if (task.repeatPattern.initialDueDate) {
      dueDate = new Date(task.repeatPattern.initialDueDate);
    } else {
      // デフォルトは翌日
      dueDate = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
    }
    
    const initialTask: TaskInstance = {
      id: `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      routineTaskId: taskId,
      projectId: newTask.projectId,
      name: newTask.name,
      description: newTask.description,
      occurrenceDate: new Date(),
      dueDate: dueDate,
      assigneeIds: newTask.assigneeIds,
      status: 'pending',
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    setAllTaskInstances(prev => [...prev, initialTask]);
  };

  // ルーチンタスク更新
  const updateRoutineTask = (taskId: string, updates: Partial<RoutineTask>) => {
    setAllRoutineTasks(prev =>
      prev.map(rt => (rt.id === taskId ? { ...rt, ...updates, updatedAt: new Date() } : rt))
    );
  };

  // ルーチンタスク削除
  const deleteRoutineTask = (taskId: string) => {
    setAllRoutineTasks(prev => prev.filter(rt => rt.id !== taskId));
    setAllTaskInstances(prev => prev.filter(t => t.routineTaskId !== taskId));
  };

  // タスクインスタンス更新
  const updateTaskInstance = (taskId: string, updates: Partial<TaskInstance>) => {
    setAllTaskInstances(prev =>
      prev.map(t => (t.id === taskId ? { ...t, ...updates, updatedAt: new Date() } : t))
    );
  };

  // タスクインスタンス削除
  const deleteTaskInstance = (taskId: string) => {
    setAllTaskInstances(prev => prev.filter(t => t.id !== taskId));
    
    // アクションヒストリーに記録
    const action: ActionHistory = {
      id: `action-${Date.now()}`,
      tenantId: tenant.id,
      targetType: 'task_instance',
      targetId: taskId,
      actionType: 'deleted',
      executorId: currentUser!.id,
      executedAt: new Date(),
    };
    setAllActionHistory(prev => [action, ...prev]);
  };

  // タスクテンプレート追加
  const addTaskTemplate = (template: Omit<TaskTemplate, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newTemplate: TaskTemplate = {
      ...template,
      id: `template-${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setAllTaskTemplates(prev => [...prev, newTemplate]);
  };

  // タスクテンプレート更新
  const updateTaskTemplate = (templateId: string, updates: Partial<TaskTemplate>) => {
    setAllTaskTemplates(prev =>
      prev.map(tg => (tg.id === templateId ? { ...tg, ...updates, updatedAt: new Date().toISOString() } : tg))
    );
  };

  // タスクテンプレート削除
  const deleteTaskTemplate = (templateId: string) => {
    setAllTaskTemplates(prev => prev.filter(tg => tg.id !== templateId));
  };

  // タスクテンプレートにタスクを追加
  const addTaskToTemplate = (templateId: string, task: Task) => {
    setAllTaskTemplates(prev =>
      prev.map(tg =>
        tg.id === templateId
          ? { ...tg, tasks: [...tg.tasks, task], updatedAt: new Date().toISOString() }
          : tg
      )
    );
  };

  // テナント更新
  const updateTenant = (tenantId: string, updates: Partial<Tenant>) => {
    setTenants(prev =>
      prev.map(t => (t.id === tenantId ? { ...t, ...updates, updatedAt: new Date() } : t))
    );
  };

  // ユーザー追加
  const addUser = (user: Omit<User, 'id' | 'createdAt'>) => {
    const newUser: User = {
      ...user,
      id: `user-${Date.now()}`,
      createdAt: new Date(),
    };
    setAllUsers(prev => [...prev, newUser]);
  };

  // ユーザー更新
  const updateUser = (userId: string, updates: Partial<User>) => {
    setAllUsers(prev =>
      prev.map(u => (u.id === userId ? { ...u, ...updates, updatedAt: new Date() } : u))
    );
  };

  // ユーザー削除
  const deleteUser = (userId: string) => {
    setAllUsers(prev => prev.filter(u => u.id !== userId));
  };

  // ゲストユーザー招待機能
  const inviteGuestToProject = (projectId: string, email: string, name: string): boolean => {
    // 既存ユーザーをチェック
    const existingUser = allUsers.find(u => u.email === email);
    
    if (existingUser) {
      // 既存ユーザーの場合はプロジェクトに追加
      setAllProjects(prev =>
        prev.map(p =>
          p.id === projectId && !p.memberIds.includes(existingUser.id)
            ? { ...p, memberIds: [...p.memberIds, existingUser.id], updatedAt: new Date() }
            : p
        )
      );
      return true;
    } else {
      // 新規ゲストユーザーを作成
      const newUser: User = {
        id: `user-${Date.now()}`,
        tenantIds: [currentTenantId],
        email,
        password: 'guest123', // デモ用仮パスワード
        name,
        role: 'guest',
        projectIds: [projectId],
        emailNotificationEnabled: true,
        createdAt: new Date(),
      };
      setAllUsers(prev => [...prev, newUser]);

      // プロジェクトにメンバーとして追加
      setAllProjects(prev =>
        prev.map(p =>
          p.id === projectId
            ? { ...p, memberIds: [...p.memberIds, newUser.id], updatedAt: new Date() }
            : p
        )
      );
      return true;
    }
  };

  // プロジェクトメンバー削除
  const removeProjectMember = (projectId: string, userId: string) => {
    setAllProjects(prev =>
      prev.map(p =>
        p.id === projectId
          ? { ...p, memberIds: p.memberIds.filter(id => id !== userId), updatedAt: new Date() }
          : p
      )
    );
    
    // ゲストユーザーの場合、projectIdsからも削除
    setAllUsers(prev =>
      prev.map(u =>
        u.id === userId && u.role === 'guest'
          ? { ...u, projectIds: u.projectIds?.filter(id => id !== projectId) }
          : u
      )
    );
  };

  const value: AppContextType = {
    // 認証関連
    isAuthenticated,
    currentUser,
    setAuthUser,
    login,
    logout,
    signUp,
    demoLogin,
    
    // OTP認証関連
    sendOTP,
    verifyOTP,
    sendSignUpOTP,
    verifySignUpOTP,
    
    // ワークスペース関連
    currentTenantId,
    tenant,
    tenants,
    workspaceMemberships,
    switchWorkspace,
    getUserWorkspaces,
    
    // 既存の機能
    users,
    projects,
    routineTasks,
    taskInstances,
    actionHistory,
    filters,
    setFilters,
    completeTask,
    uncompleteTask,
    addProject,
    updateProject,
    deleteProject,
    archiveProject,
    unarchiveProject,
    addRoutineTask,
    updateRoutineTask,
    deleteRoutineTask,
    updateTaskInstance,
    deleteTaskInstance,
    taskTemplates,
    
    // タスクテンプレート管理
    addTaskTemplate,
    updateTaskTemplate,
    deleteTaskTemplate,
    addTaskToTemplate,
    
    // テナント・ユーザー管理
    updateTenant,
    addUser,
    updateUser,
    deleteUser,
    
    // ゲストユーザー招待機能
    inviteGuestToProject,
    removeProjectMember,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
}
